import { isValid, parseISO } from 'date-fns';
import type { QuotaBalanceDto } from '@/types/dtos/entitlements/quota-balance.dto';
import type { SubscriptionPlan } from '@/types/dtos/transactions/subscription-plan.dto';
import type { SubscriptionAdditionalQuotaUnit } from '@/types/dtos/transactions/subscription.dto';
import { defaultMainSubscriptionProductKeyForCategory } from '@/helpers/organization-plan-catalog';
import { resolvePlanQuotaKeyLabel } from '@/helpers/manage-plan-feature-display';

const HOST_DEFAULT_PRODUCT_KEY = defaultMainSubscriptionProductKeyForCategory('HOST');

export function getSubscriptionBasePrice(plan: SubscriptionPlan): number {
  return plan.price;
}

function productKeyOnPlan(p: SubscriptionPlan): string {
  return p.subscriptionProductKey?.trim() || HOST_DEFAULT_PRODUCT_KEY;
}

/** Same catalog row (product key + tier + billing + entity): used to borrow overage from an org’s embedded plan. */
export function plansMatchForOverageMerge(a: SubscriptionPlan, b: SubscriptionPlan | undefined): boolean {
  if (!b) {
    return false;
  }
  if (productKeyOnPlan(a) !== productKeyOnPlan(b)) {
    return false;
  }
  return (
    a.tier === b.tier && (a.billingPeriod ?? 'MONTH') === (b.billingPeriod ?? 'MONTH') && a.entityType === b.entityType
  );
}

/** At least one non-deleted overage row with a finite, non-negative unit price. */
export function hasEffectiveOveragePricing(plan: SubscriptionPlan): boolean {
  for (const o of plan.overagePrices ?? []) {
    if ((o as { deleted?: boolean }).deleted === true) {
      continue;
    }
    if (!String(o.quotaKey ?? '').trim()) {
      continue;
    }
    const raw = o.pricePerUnit;
    const n = typeof raw === 'number' ? raw : Number(raw);
    if (Number.isFinite(n) && n > 0) {
      return true;
    }
  }
  return false;
}

/**
 * Normalizes overage rows for UI (matches API sanitization: drop deleted rows, invalid prices, trim keys).
 */
export function normalizeSubscriptionPlanOverage(plan: SubscriptionPlan): SubscriptionPlan {
  const rows =
    plan.overagePrices
      ?.map((o) => {
        if ((o as { deleted?: boolean }).deleted === true) {
          return null;
        }
        const key = String(o.quotaKey ?? '').trim();
        if (!key) {
          return null;
        }
        const raw = o.pricePerUnit;
        const n = typeof raw === 'number' ? raw : Number(raw);
        if (!Number.isFinite(n) || n < 0) {
          return null;
        }
        const stepRaw = o.step;
        const stepN = typeof stepRaw === 'number' ? stepRaw : Number(stepRaw);
        const step = Number.isFinite(stepN) && stepN >= 1 ? Math.floor(stepN) : undefined;
        return { quotaKey: key, pricePerUnit: n, ...(step != null ? { step } : {}) };
      })
      .filter((x): x is { quotaKey: string; pricePerUnit: number; step?: number } => x != null) ?? [];
  return { ...plan, overagePrices: rows.length ? rows : undefined };
}

/**
 * When the catalog row has no **usable** overage (empty, all deleted, or no valid `pricePerUnit`) but the
 * organization’s embedded plan matches and has rates, copy those over for UI pricing.
 */
export function mergeOverageFromMatchingSubscriptionSnapshot(
  catalogPlan: SubscriptionPlan,
  subscriptionPlan: SubscriptionPlan | undefined,
): SubscriptionPlan {
  /** Free tier catalog rows never borrow overage rates from the embedded plan (would wrongly enable paid add-ons). */
  if (catalogPlan.tier === 'FREE') {
    return catalogPlan;
  }
  if (!plansMatchForOverageMerge(catalogPlan, subscriptionPlan)) {
    return catalogPlan;
  }
  if (hasEffectiveOveragePricing(catalogPlan)) {
    return catalogPlan;
  }
  if (!subscriptionPlan || !hasEffectiveOveragePricing(subscriptionPlan)) {
    return catalogPlan;
  }
  return { ...catalogPlan, overagePrices: subscriptionPlan.overagePrices };
}

export function getOveragePricePerUnit(plan: SubscriptionPlan, quotaKey: string): number | undefined {
  const target = quotaKey.trim().toLowerCase();
  for (const o of plan.overagePrices ?? []) {
    if ((o as { deleted?: boolean }).deleted === true) {
      continue;
    }
    if (
      String(o.quotaKey ?? '')
        .trim()
        .toLowerCase() !== target
    ) {
      continue;
    }
    const raw = o.pricePerUnit;
    if (raw === undefined || raw === null) {
      continue;
    }
    const n = typeof raw === 'number' ? raw : Number(raw);
    if (!Number.isFinite(n) || n <= 0) {
      continue;
    }
    return n;
  }
  return undefined;
}

/** UI +/- click size from catalog `overagePrices[].step` (defaults to 1). */
export function getOverageStepForKey(plan: SubscriptionPlan, quotaKey: string): number {
  const target = quotaKey.trim().toLowerCase();
  for (const o of plan.overagePrices ?? []) {
    if ((o as { deleted?: boolean }).deleted === true) {
      continue;
    }
    if (
      String(o.quotaKey ?? '')
        .trim()
        .toLowerCase() !== target
    ) {
      continue;
    }
    const raw = o.step;
    if (raw === undefined || raw === null) {
      return 1;
    }
    const n = typeof raw === 'number' ? raw : Number(raw);
    if (!Number.isFinite(n) || n < 1) {
      return 1;
    }
    return Math.floor(n);
  }
  return 1;
}

/** Next full-period renewal (base + add-on quantities × overage), matches backend `computeOrganizationRenewalAmount`. */
export function computeOrganizationRenewalAmount(
  plan: SubscriptionPlan,
  additionalQuotaUnits: SubscriptionAdditionalQuotaUnit[] | undefined,
): number {
  let amount = getSubscriptionBasePrice(plan);
  for (const row of additionalQuotaUnits ?? []) {
    if (row.quantity <= 0) continue;
    const p = getOveragePricePerUnit(plan, row.quotaKey);
    if (p !== undefined) {
      amount += row.quantity * p;
    }
  }
  return Math.round(amount * 100) / 100;
}

/**
 * Non-active plan cards: base catalog price plus local preview extras (units above included) × overage price per key.
 * Mirrors {@link computeOrganizationRenewalAmount} but uses stepper “extra above included” state.
 */
export function computePreviewSubscriptionPrice(
  plan: SubscriptionPlan,
  localQuotaExtra: Record<string, number>,
): number {
  let amount = getSubscriptionBasePrice(plan);
  for (const [quotaKey, extra] of Object.entries(localQuotaExtra)) {
    if (extra <= 0) continue;
    const p = getOveragePricePerUnit(plan, quotaKey);
    if (p !== undefined) {
      amount += extra * p;
    }
  }
  return Math.round(amount * 100) / 100;
}

export function includedQuantityForKey(plan: SubscriptionPlan, quotaKey: string): number {
  const target = quotaKey.trim().toLowerCase();
  const row = plan.includedQuotas?.find(
    (q) =>
      String(q.quotaKey ?? '')
        .trim()
        .toLowerCase() === target,
  );
  if (row !== undefined) {
    return row.quantity;
  }
  if (quotaKey === 'members' && plan.features && typeof plan.features === 'object') {
    const m = (plan.features as { members?: number }).members;
    if (typeof m === 'number') {
      return m;
    }
  }
  return 0;
}

/** Optional catalog cap: maximum total units for this quota key on this plan (`maxQuotas[].quantity`). */
export function maxQuantityForKey(plan: SubscriptionPlan, rawQuotaKey: string): number | undefined {
  const target = rawQuotaKey.trim().toLowerCase();
  for (const r of plan.maxQuotas ?? []) {
    if (
      String(r.quotaKey ?? '')
        .trim()
        .toLowerCase() !== target
    ) {
      continue;
    }
    const q = typeof r.quantity === 'number' ? r.quantity : Number(r.quantity);
    if (!Number.isFinite(q) || q < 0) {
      continue;
    }
    return q;
  }
  return undefined;
}

export function getAdditionalUnits(
  additionalQuotaUnits: SubscriptionAdditionalQuotaUnit[] | undefined,
  quotaKey: string,
): number {
  return additionalQuotaUnits?.find((u) => u.quotaKey === quotaKey)?.quantity ?? 0;
}

/** Keys that appear in catalog included quotas, overage prices, or legacy `features.members`. */
export function collectQuotaKeysFromPlan(plan: SubscriptionPlan): string[] {
  const keys = new Set<string>();
  for (const q of plan.includedQuotas ?? []) {
    keys.add(q.quotaKey);
  }
  for (const o of plan.overagePrices ?? []) {
    keys.add(o.quotaKey);
  }
  for (const m of plan.maxQuotas ?? []) {
    const k = String(m.quotaKey ?? '').trim();
    if (k) {
      keys.add(k);
    }
  }
  if (
    keys.size === 0 &&
    plan.features &&
    typeof plan.features === 'object' &&
    typeof (plan.features as { members?: number }).members === 'number'
  ) {
    keys.add('members');
  }
  return [...keys];
}

/** Shown capacity: ledger grant when present, else included + purchased add-on units. */
export function getQuotaDisplayTotal(
  plan: SubscriptionPlan,
  quotaKey: string,
  balance: QuotaBalanceDto | undefined,
  additionalQuotaUnits: SubscriptionAdditionalQuotaUnit[] | undefined,
): number {
  if (balance) {
    return balance.totalGranted;
  }
  return includedQuantityForKey(plan, quotaKey) + getAdditionalUnits(additionalQuotaUnits, quotaKey);
}

/**
 * Purchased capacity for stepper deltas (Event): catalog snapshot / plan includes + paid
 * `additionalQuotaUnits` only — never entitlement balances (those can include admin grants).
 */
export function getPurchasedQuotaTotal(params: {
  quotaKey: string;
  /** Frozen at fulfill — preferred commercial baseline. */
  catalogSnapshotIncluded?: { quotaKey: string; quantity: number }[];
  /** Live booked plan (e.g. Free) when snapshot missing. */
  bookedPlan?: SubscriptionPlan | null;
  additionalQuotaUnits?: SubscriptionAdditionalQuotaUnit[];
}): number {
  const key = String(params.quotaKey ?? '').trim();
  if (!key) return 0;
  const fromSnap = params.catalogSnapshotIncluded?.find((q) => String(q.quotaKey ?? '').trim() === key)?.quantity;
  const included =
    typeof fromSnap === 'number' && Number.isFinite(fromSnap)
      ? Math.max(0, fromSnap)
      : params.bookedPlan
        ? includedQuantityForKey(params.bookedPlan, key)
        : 0;
  return included + getAdditionalUnits(params.additionalQuotaUnits, key);
}

/**
 * Minimum selectable total for the manage-plan stepper: plan-included quantity, never below currently
 * **consumed** units (already-assigned seats can't be released by lowering the cart). The active grant
 * itself is NOT a floor — users may schedule a reduction below the current grant which takes effect at
 * next renewal (downgrade alert tells them to free up seats before then).
 */
export function computeQuotaFloor(
  plan: SubscriptionPlan,
  quotaKey: string,
  balance: QuotaBalanceDto | undefined,
): number {
  const included = includedQuantityForKey(plan, quotaKey);
  const consumed = balance?.totalConsumed ?? 0;
  return Math.max(included, consumed);
}

/**
 * Human label for quota keys from the catalog / cart (`host_default:members`, `members`, …).
 * Uses `subscription.organization.planPage.quotaKey.<segments>` (colon → nested path), then
 * falls back to `planPage.quota.members` / `devices` by suffix, then the raw key.
 */
export function resolveQuotaKeyDisplayLabel(
  t: (key: string, params?: Record<string, string | number>) => string,
  rawQuotaKey: string,
): string {
  const quotaKey = String(rawQuotaKey ?? '').trim();
  if (!quotaKey) {
    return '';
  }
  const segments = quotaKey
    .split(':')
    .map((s) => s.trim())
    .filter(Boolean);
  if (segments.length === 0) {
    return quotaKey;
  }
  const dottedPath =
    segments.length >= 2
      ? `subscription.organization.planPage.quotaKey.${segments.join('.')}`
      : `subscription.organization.planPage.quotaKey.${segments[0]}`;
  const resolved = t(dottedPath);
  if (resolved !== dottedPath) {
    return resolved;
  }
  const suffix = segments.length >= 1 ? segments[segments.length - 1]! : quotaKey;
  if (suffix === 'members') {
    return t('subscription.organization.planPage.quota.members');
  }
  if (suffix === 'devices') {
    return t('subscription.organization.planPage.quota.devices');
  }
  return quotaKey;
}

export type QuotaPluralGroup = 'members' | 'devices' | 'generic';

/** Last path segment of a namespaced quota key, mapped to plural copy groups (`members` / `devices`). */
export function quotaPluralGroupFromQuotaKey(rawQuotaKey: string): QuotaPluralGroup {
  const segs = String(rawQuotaKey ?? '')
    .split(':')
    .map((s) => s.trim())
    .filter(Boolean);
  const last = (segs.length ? segs[segs.length - 1]! : String(rawQuotaKey)).toLowerCase();
  if (last === 'members') {
    return 'members';
  }
  if (last === 'devices') {
    return 'devices';
  }
  return 'generic';
}

/** Primary quota line in steppers / checkout (singular/plural by quota kind). */
export function formatQuotaStepperCountLine(
  t: (key: string, params?: Record<string, string | number>) => string,
  rawQuotaKey: string,
  count: number,
): string {
  const planLabel = resolvePlanQuotaKeyLabel(t, rawQuotaKey, count);
  if (planLabel) {
    return t('subscription.organization.planPage.quotaStepperCountGeneric', { count, label: planLabel });
  }
  const g = quotaPluralGroupFromQuotaKey(rawQuotaKey);
  if (g !== 'generic') {
    const k = `subscription.organization.planPage.quotaStepperCount_${g}`;
    const r = t(k, { count });
    if (r !== k) {
      return r;
    }
  }
  const label = resolveQuotaKeyDisplayLabel(t, rawQuotaKey);
  return t('subscription.organization.planPage.quotaStepperCountGeneric', { count, label });
}

/** Secondary “current booked” line (singular/plural by quota kind). */
export function formatQuotaCheckoutCurrentBookedLine(
  t: (key: string, params?: Record<string, string | number>) => string,
  rawQuotaKey: string,
  count: number,
): string {
  const planLabel = resolvePlanQuotaKeyLabel(t, rawQuotaKey, count);
  if (planLabel) {
    return t('subscription.organization.planPage.checkoutQuotaCurrentBookedGeneric', {
      count,
      label: planLabel,
    });
  }
  const g = quotaPluralGroupFromQuotaKey(rawQuotaKey);
  if (g !== 'generic') {
    const k = `subscription.organization.planPage.checkoutQuotaCurrentBooked_${g}`;
    const r = t(k, { count });
    if (r !== k) {
      return r;
    }
  }
  const label = resolveQuotaKeyDisplayLabel(t, rawQuotaKey);
  return t('subscription.organization.planPage.checkoutQuotaCurrentBookedGeneric', { count, label });
}

/** Same date style as checkout renewal labels (`MMM d, yyyy`). */
/**
 * `periodEnd` / `validUntil` is the renewal instant (exclusive end of the half-open billing period
 * `[start, end)`). For user-facing copy — e.g. the downgrade alert "unassign N seats by …" — we want
 * the **inclusive last service day** (the calendar day before renewal in UTC), not the renewal day
 * itself. Otherwise customers are told they have one more day than they actually do.
 */
export function formatSubscriptionInstancePeriodEndDisplay(
  subscription: { periodEnd?: string; validUntil?: string } | undefined,
): string | null {
  const raw = subscription?.periodEnd ?? subscription?.validUntil;
  if (raw == null || typeof raw !== 'string' || !raw.trim()) {
    return null;
  }
  try {
    const d = parseISO(raw.trim());
    if (!isValid(d)) {
      return null;
    }
    return formatInclusiveLastServiceDayFromExclusiveEndUtc(d);
  } catch {
    return null;
  }
}

/**
 * Signed delta vs currently booked capacity (Zoom-style subline under the stepper).
 * `previewTotal` is the selection on the target plan card; `bookedTotal` is the org’s current grant for that key.
 */
export function formatQuotaStepperDeltaVsBooked(
  t: (key: string, params?: Record<string, string | number>) => string,
  rawQuotaKey: string,
  previewTotal: number,
  bookedTotal: number,
): string {
  const delta = previewTotal - bookedTotal;
  if (delta === 0) {
    const k = 'subscription.organization.planPage.quotaStepperDeltaNoChange';
    const r = t(k);
    return r !== k ? r : 'No change from your current plan';
  }
  const abs = Math.abs(delta);
  const sign = delta > 0 ? '+' : '\u2212';
  const planLabel = resolvePlanQuotaKeyLabel(t, rawQuotaKey, abs);
  if (planLabel) {
    return t('subscription.organization.planPage.quotaStepperDeltaGeneric', {
      count: abs,
      sign,
      label: planLabel,
    });
  }
  const g = quotaPluralGroupFromQuotaKey(rawQuotaKey);
  if (g !== 'generic') {
    const k = `subscription.organization.planPage.quotaStepperDelta_${g}`;
    const r = t(k, { count: abs, sign });
    if (r !== k) {
      return r;
    }
  }
  const label = resolveQuotaKeyDisplayLabel(t, rawQuotaKey);
  return t('subscription.organization.planPage.quotaStepperDeltaGeneric', { count: abs, sign, label });
}

export function formatQuotaDowngradeProceedAlert(
  t: (key: string, params?: Record<string, string | number>) => string,
  rawQuotaKey: string,
  /** Booked total minus selected total (positive). */
  reduction: number,
  periodEndDisplay: string,
): string {
  const count = Math.max(0, reduction);
  const planLabel = resolvePlanQuotaKeyLabel(t, rawQuotaKey, count);
  if (planLabel) {
    const genericKey = 'subscription.organization.planPage.quotaDowngradeProceedInfoGeneric';
    const generic = t(genericKey, { count, date: periodEndDisplay, label: planLabel });
    if (generic !== genericKey) {
      return generic;
    }
  }
  const g = quotaPluralGroupFromQuotaKey(rawQuotaKey);
  if (g !== 'generic') {
    const k = `subscription.organization.planPage.quotaDowngradeProceedInfo_${g}`;
    const r = t(k, { count, date: periodEndDisplay });
    if (r !== k) {
      return r;
    }
  }
  const label = resolveQuotaKeyDisplayLabel(t, rawQuotaKey);
  const genericKey = 'subscription.organization.planPage.quotaDowngradeProceedInfoGeneric';
  const generic = t(genericKey, { count, date: periodEndDisplay, label });
  return generic !== genericKey
    ? generic
    : `On ${periodEndDisplay}, ${count} least-recently-active ${label} will be released automatically to fit your new plan.`;
}

const SUBSCRIPTION_BILLING_DATE_UTC: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
};

/**
 * Format a billing instant with its **UTC** calendar date (`periodStart` / `periodEnd` are ISO UTC boundaries).
 * Use for subscription copy so “last day before renewal” does not shift when `periodEnd` is midnight Z and the
 * viewer is in a positive-offset zone.
 */
export function formatSubscriptionBillingInstantUtcMedium(d: Date): string {
  return new Intl.DateTimeFormat(undefined, SUBSCRIPTION_BILLING_DATE_UTC).format(d);
}

const MS_PER_DAY = 86_400_000;

/**
 * Last inclusive **UTC** calendar day of service.
 *
 * Subscription rows model service as half-open `[periodStart, periodEnd)` in UTC, but the persisted
 * `periodEnd` may be **any time of day** (it is `addMonths(periodStart, 1)` — i.e. the same UTC
 * wall-clock as `periodStart`). For copy like “… through {date} (inclusive)” we always want the
 * **calendar day before the renewal day** so the renewal day itself is never shown as serviced. We
 * therefore take `startOfUtcDay(exclusiveEnd) - 1 day`, which is correct regardless of the hours /
 * minutes component of `exclusiveEnd` and matches the user’s mental model of monthly billing
 * (e.g. period Apr 27 → May 27 ⇒ last service day = May 26).
 */
export function formatInclusiveLastServiceDayFromExclusiveEndUtc(exclusiveEnd: Date): string {
  const startOfRenewalDayUtcMs = Date.UTC(
    exclusiveEnd.getUTCFullYear(),
    exclusiveEnd.getUTCMonth(),
    exclusiveEnd.getUTCDate(),
  );
  return formatSubscriptionBillingInstantUtcMedium(new Date(startOfRenewalDayUtcMs - MS_PER_DAY));
}
